import React, { useRef, useState, useCallback, useEffect } from 'react';
import './ImageCropper.css';

const CONTAINER_W = 520;
const CONTAINER_H = 520;
const CROP_SIZE = 320;
const CROP_LEFT = (CONTAINER_W - CROP_SIZE) / 2;   // 100
const CROP_TOP  = (CONTAINER_H - CROP_SIZE) / 2;   // 100
const MAX_SCALE = 10;

const ImageCropper = ({ file, onConfirm, onCancel }) => {
  const imgRef       = useRef(null);
  const containerRef = useRef(null);
  const [naturalSize, setNaturalSize] = useState(null);
  const [scale, setScale]   = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragging          = useRef(false);
  const dragStart         = useRef({ x: 0, y: 0 });
  const offsetAtDragStart = useRef({ x: 0, y: 0 });
  const [objectUrl, setObjectUrl] = useState(null);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    setObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // Minimum scale: image smallest dimension = CROP_SIZE / 2
  const minScale = naturalSize
    ? (CROP_SIZE / 2) / Math.min(naturalSize.w, naturalSize.h)
    : 0.05;

  const handleImageLoad = useCallback(() => {
    const img = imgRef.current;
    if (!img) return;
    const { naturalWidth: nw, naturalHeight: nh } = img;
    setNaturalSize({ w: nw, h: nh });
    // Start with image covering the crop area
    const coverScale = Math.max(CROP_SIZE / nw, CROP_SIZE / nh);
    setScale(coverScale);
    setOffset({ x: 0, y: 0 });
  }, []);

  // Mouse drag
  const onMouseDown = useCallback((e) => {
    e.preventDefault();
    dragging.current = true;
    dragStart.current = { x: e.clientX, y: e.clientY };
    offsetAtDragStart.current = { ...offset };
  }, [offset]);

  const onMouseMove = useCallback((e) => {
    if (!dragging.current) return;
    setOffset({
      x: offsetAtDragStart.current.x + (e.clientX - dragStart.current.x),
      y: offsetAtDragStart.current.y + (e.clientY - dragStart.current.y)
    });
  }, []);

  const onMouseUp = useCallback(() => { dragging.current = false; }, []);

  // Touch drag
  const onTouchStart = useCallback((e) => {
    const t = e.touches[0];
    dragging.current = true;
    dragStart.current = { x: t.clientX, y: t.clientY };
    offsetAtDragStart.current = { ...offset };
  }, [offset]);

  const onTouchMove = useCallback((e) => {
    if (!dragging.current) return;
    e.preventDefault();
    const t = e.touches[0];
    setOffset({
      x: offsetAtDragStart.current.x + (t.clientX - dragStart.current.x),
      y: offsetAtDragStart.current.y + (t.clientY - dragStart.current.y)
    });
  }, []);

  const onTouchEnd = useCallback(() => { dragging.current = false; }, []);

  // Scroll to zoom
  const onWheel = useCallback((e) => {
    e.preventDefault();
    const factor = e.deltaY < 0 ? 1.08 : 0.93;
    setScale(prev => Math.min(MAX_SCALE, Math.max(minScale, prev * factor)));
  }, [minScale]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [onWheel]);

  const handleConfirm = useCallback(() => {
    if (!naturalSize || !imgRef.current) return;
    const { w: nw, h: nh } = naturalSize;

    // Image bounds on screen (relative to container top-left)
    const imgCX    = CONTAINER_W / 2 + offset.x;
    const imgCY    = CONTAINER_H / 2 + offset.y;
    const imgLeft  = imgCX - (nw * scale) / 2;
    const imgTop   = imgCY - (nh * scale) / 2;
    const imgRight = imgLeft + nw * scale;
    const imgBot   = imgTop  + nh * scale;

    // Crop box bounds
    const cropRight = CROP_LEFT + CROP_SIZE;
    const cropBot   = CROP_TOP  + CROP_SIZE;

    const canvas = document.createElement('canvas');
    canvas.width  = 400;
    canvas.height = 400;
    const ctx = canvas.getContext('2d');

    // Fill black — covers areas where the image doesn't reach the crop box
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, 400, 400);

    // Only draw the part of the image that overlaps with the crop box
    const olLeft  = Math.max(imgLeft,  CROP_LEFT);
    const olTop   = Math.max(imgTop,   CROP_TOP);
    const olRight = Math.min(imgRight, cropRight);
    const olBot   = Math.min(imgBot,   cropBot);

    if (olRight > olLeft && olBot > olTop) {
      const srcX = (olLeft - imgLeft) / scale;
      const srcY = (olTop  - imgTop)  / scale;
      const srcW = (olRight - olLeft) / scale;
      const srcH = (olBot   - olTop)  / scale;

      const dstX = ((olLeft  - CROP_LEFT) / CROP_SIZE) * 400;
      const dstY = ((olTop   - CROP_TOP)  / CROP_SIZE) * 400;
      const dstW = ((olRight - olLeft)    / CROP_SIZE) * 400;
      const dstH = ((olBot   - olTop)     / CROP_SIZE) * 400;

      ctx.drawImage(imgRef.current, srcX, srcY, srcW, srcH, dstX, dstY, dstW, dstH);
    }

    canvas.toBlob(blob => onConfirm(blob), 'image/jpeg', 0.92);
  }, [naturalSize, offset, scale, onConfirm]);

  const imgStyle = {
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px)) scale(${scale})`,
    transformOrigin: 'center center',
    userSelect: 'none',
    pointerEvents: 'none',
    maxWidth: 'none',
    maxHeight: 'none'
  };

  return (
    <div className="cropper-overlay">
      <div className="cropper-modal">
        <div
          ref={containerRef}
          className="cropper-container"
          style={{ width: CONTAINER_W, height: CONTAINER_H }}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {objectUrl && (
            <img
              ref={imgRef}
              src={objectUrl}
              alt="crop preview"
              style={imgStyle}
              onLoad={handleImageLoad}
              draggable={false}
            />
          )}

          {/* Four overlay panels around the crop square */}
          <div className="crop-overlay crop-overlay-top"    style={{ height: CROP_TOP }} />
          <div className="crop-overlay crop-overlay-bottom" style={{ height: CROP_TOP }} />
          <div className="crop-overlay crop-overlay-left"   style={{ top: CROP_TOP, height: CROP_SIZE, width: CROP_LEFT }} />
          <div className="crop-overlay crop-overlay-right"  style={{ top: CROP_TOP, height: CROP_SIZE, width: CROP_LEFT }} />

          {/* Crop border with rule-of-thirds grid */}
          <div
            className="crop-border"
            style={{ left: CROP_LEFT, top: CROP_TOP, width: CROP_SIZE, height: CROP_SIZE }}
          >
            <div className="crop-grid">
              <div /><div /><div />
              <div /><div /><div />
              <div /><div /><div />
            </div>
            <div className="crop-corner crop-corner-tl" />
            <div className="crop-corner crop-corner-tr" />
            <div className="crop-corner crop-corner-bl" />
            <div className="crop-corner crop-corner-br" />
          </div>

          {/* Title + hint — sits over the top dark band */}
          <div className="cropper-ui-top">
            <h3 className="cropper-title">Adjust Image Crop</h3>
            <p className="cropper-hint">Scroll to zoom · Drag to reposition</p>
          </div>

          {/* Buttons — sits over the bottom dark band, stops drag propagation */}
          <div
            className="cropper-ui-bottom"
            onMouseDown={e => e.stopPropagation()}
            onTouchStart={e => e.stopPropagation()}
          >
            <button className="btn btn-secondary" onClick={onCancel}>Cancel</button>
            <button className="btn btn-primary" onClick={handleConfirm} disabled={!naturalSize}>
              Confirm Crop
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageCropper;
